import axios from "axios";
import { toast } from "sonner";
import { adoptNewerPersistedTokens, useAuthStore } from "@/shared/state/authstore";
import { redirectToLogin } from "@/shared/lib/loginRedirect";

let inFlight: Promise<string> | null = null;

function endSession(message: string): never {
    useAuthStore.getState().clearAuth();
    toast.error(message);
    redirectToLogin();
    throw new Error(message);
}

async function refreshOnce(staleAccessToken: string | null): Promise<string> {
    adoptNewerPersistedTokens();
    const store = useAuthStore.getState();

    if (store.accessToken && store.accessToken !== staleAccessToken && !store.isAccessTokenExpired()) {
        return store.accessToken;
    }

    const refreshToken = store.refreshToken;

    if (!refreshToken || store.isRefreshTokenExpired()) {
        endSession("Session expired. Please login again.");
    }

    const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/v1/token/refresh`,
        { refreshToken },
        { headers: { "X-Portal": "gms" } },
    );

    const tokens = response.data;
    useAuthStore.getState().updateTokens({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        accessTokenExpiresAt: tokens.accessTokenExpiresAt,
        refreshTokenExpiresAt: tokens.refreshTokenExpiresAt,
        availableAccounts: tokens.availableAccounts,
    });

    return tokens.accessToken as string;
}

export function ensureFreshToken(force = false): Promise<string> {
    const store = useAuthStore.getState();

    if (!force && store.accessToken && !store.isAccessTokenExpired()) {
        return Promise.resolve(store.accessToken);
    }

    if (inFlight) {
        return inFlight;
    }

    inFlight = refreshOnce(store.accessToken).finally(() => {
        inFlight = null;
    });

    return inFlight;
}
